import { scaleBand, scaleLinear, max, pointer } from 'd3';
import { ChartCtrParams, MarginCtrParams } from '../types';

import Svg from '../Base/svg';
import Axis from '../Base/axis';
import Margin from '../Base/margin';
import Utils from '../Utils';
import ToolTip from '../ToolTip';
import Dims from '../Base/dims';
import Data from '../Base/data';
import BarChartStyle from './style';

/**
 * Class representing a Bar Chart
 */
export class BarChart {
  private elementId: string;
  private element: Svg;

  private dims: Dims;
  private margin: Margin;

  private toolTip: ToolTip;
  private useToolTip = false;
  private animators: any;

  public data: Data;
  // TODO: need to sort out the cut point for the 'fields'
  // management. But a cut point has yet to reveal itself...
  public fields: any; // x or y
  private _xField = 'x';
  private _yField = 'y';

  // TODO: Turn into axis group??
  // public axisGroup: AxisGroup;
  public xAxis: Axis;
  public yAxis: Axis;

  public style: BarChartStyle;

  private barSelectionIdentifier = 'bars';

  /**
   * Constructs a new bar chart instance
   * ```ts
   * const data = [{ x: 'US', y: 12394 }, ... ]
   * const barchart = new AchoViz.BarChart({ elementId: '#barchart', data });
   * ```
   * @param params - constructor params
   * @returns instance of a BarChart
   */
  constructor(params: ChartCtrParams = {}) {
    this.elementId = params.elementId;
    this.margin = new Margin(params.margin);
    this.data = new Data(params.data);
    this.style = new BarChartStyle();

    this.dims = new Dims(params.height, params.width);

    this._xField = params.xField ?? 'x';
    this._yField = params.yField ?? 'y';

    this.yAxis = new Axis({ loc: 'left' });
    this.xAxis = new Axis({ loc: 'bottom' });
    this._useAutoAxis();

    this.useToolTip = params.useToolTip ?? false;
    this._useTipConfig();
  }

  /**
   * Handles drawing, mounting, and ultimately rendering the chart
   */
  public render() {
    const { elementId, margin } = this;
    const { height, width } = this.dims.both();

    this.element = new Svg({ elementId, height, width, margin });
    this.element.draw();

    this._drawXAxis();
    this._drawYAxis();
    this._drawBars();
    this._useInteractions();
  }

  /**
   * Draws the x axis
   */
  private _drawXAxis() {
    const { d3Svg } = this.element;
    const { height: h } = this.dims.marDims(this.margin);
    // const h = this.height - this.margin.top - this.margin.bottom;
    const translate = 'translate(0,' + h + ')';
    const ax = Utils.drawAxis(d3Svg, this.xAxis, { translate });
    // TODO: tick item display should be controllable by the user
    Utils.drawAxisLabels(ax, this.xAxis);
    // ax.selectAll('text').style('display', 'none');
  }

  /**
   * Draws the y axis
   */
  private _drawYAxis() {
    const { d3Svg } = this.element;
    const ax = Utils.drawAxis(d3Svg, this.yAxis);
    // ax.selectAll('text').style('display', 'none');
  }

  /**
   * Draws the bars with respect to data provided to the instance
   */
  private _drawBars() {
    const { _xField, _yField, xAxis, yAxis, style } = this;
    const { height: h } = this.dims.marDims(this.margin);

    const _data = this.data.consume();

    const attr = {
      identifier: this.barSelectionIdentifier,
      x: function (d) {
        return xAxis.scale(d[_xField]);
      },
      y: function (d) {
        return yAxis.scale(d[_yField]);
      },
      width: xAxis.scale.bandwidth(),
      height: function (d) {
        return h - yAxis.scale(d[_yField]);
      },
      fill: style.barColor,
    };

    const params = {
      identifier: this.barSelectionIdentifier,
      attr,
    };

    Utils.drawBars(this.element.d3Svg, _data, params);
  }

  /**
   * Event handler triggered when mouse is detected over a bar
   * @param e
   * @returns
   */
  private _onOverBar(e) {
    if (!this.useToolTip) return;
    // this.toolTipInstance.show();
    this.toolTip.show();
  }

  /**
   * Event handler triggered when mouse has left the bar
   * @param e
   * @returns
   */
  private _onLeaveBar(e) {
    if (!this.useToolTip) return;
    this.toolTip.hide();
  }

  /**
   * Event handler triggered when mouse moves to another bar
   * @param e
   * @returns
   */
  private _onMoveBar(e) {
    if (!this.useToolTip) return;

    const loc = pointer(e);
    this.toolTip.updateLoc(loc[0], loc[1]);
  }

  /**
   * Sets the instance's margin properties
   * @param value
   */
  public setMargin(value: number | MarginCtrParams) {
    this.margin = new Margin(value);
    // whenever margins change, need to reconfigure the axis (domains and ranges)
    this._useAutoAxis();
  }

  /**
   * Automatically configures the axis scales, domain, and ranges
   */
  private _useAutoAxis() {
    const { height: h, width: w } = this.dims.marDims(this.margin);

    const xDomain = this.data.consumeByField(this._xField);
    const xRange = [0, w];
    const xScale = scaleBand().range(xRange).domain(xDomain).padding(0.2);
    this.xAxis.setDomain(xDomain);
    this.xAxis.setScale(xScale);

    const yDomain = [0, max(this.data.consumeByField(this._yField))] as Iterable<number>;

    const yRange = [h, 0];
    const yScale = scaleLinear().domain(yDomain).range(yRange);
    this.yAxis.setDomain(yDomain);
    this.yAxis.setScale(yScale);
  }

  /**
   * Hook to automatically configure the chart's tool tip instance
   * @returns
   */
  private _useTipConfig() {
    if (!this.useToolTip) return;
    // TODO: should be able to supply other options to the tool tip here
    this.toolTip = new ToolTip({ parentId: this.elementId });
  }

  /**
   * Hook to automatically register bar related mouse events
   */
  private _useInteractions() {
    if (!this.useToolTip) return;

    const events = {
      mouseover: this._onOverBar.bind(this),
      mousemove: this._onMoveBar.bind(this),
      mouseleave: this._onLeaveBar.bind(this),
    };

    Utils.bindBarEvents(this.element.d3Svg, 'rect', events);
  }
}
